import {
  Alert,
  App as AntdApp,
  Modal,
  Form,
  Select,
  DatePicker,
  Upload,
  Button,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import type { UploadFile } from "antd/es/upload/interface";
import dayjs from "dayjs";

import { waypointService } from "../../../services/api/waypointService";
import { useAssignableAuditors } from "./useAssignableAuditors";

interface UploadWaypointsModalProps {
  visible: boolean;
  onClose: () => void;
  planningId: number;
  auditors: Array<{ id: number; name: string }>;
  auditCompanyId?: number | null;
  countryId?: number | null;
  isAuditorOnly?: boolean;
  currentUser?: { id: number; name: string } | null;
  onSuccess?: () => void;
}

interface WaypointFormValues {
  waypoint_date: dayjs.Dayjs;
  auditor_id: number;
  gpx_file: UploadFile[];
}

/**
 * Modal for uploading a waypoints GPX file for a planning group.
 *
 * Mirrors `RouteUploadModal` visually — same Modal footer (`onOk`),
 * same field ordering (date, auditor, GPX file via an `Upload`
 * button, not a Dragger), same auditor picker. The previous version
 * stored the raw `File` object into `fileList` from `beforeUpload`,
 * which left `fileList[0].originFileObj` undefined and silently sent
 * no file in the multipart body — the backend returned 400 "GPX file
 * is required" with no obvious clue why. We now follow the antd
 * `onChange` pattern so `originFileObj` is always populated.
 */
export const UploadWaypointsModal: React.FC<UploadWaypointsModalProps> = ({
  visible,
  onClose,
  planningId,
  auditors,
  auditCompanyId,
  countryId,
  isAuditorOnly = false,
  currentUser,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();
  const [form] = Form.useForm<WaypointFormValues>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  // Persistent error banner inside the modal — message toasts
  // disappear in 3s, which is the user's main complaint ("the error
  // is only in the console"). The banner stays until they fix and
  // retry, or close the modal.
  const [submitError, setSubmitError] = useState<string | null>(null);

  const auditorPicker = useAssignableAuditors({
    visible,
    auditCompanyId,
    countryId,
    isAuditorOnly,
    currentUser,
    fallback: auditors,
  });

  useEffect(() => {
    if (visible && auditorPicker.locked && auditorPicker.defaultValue != null) {
      form.setFieldsValue({ auditor_id: auditorPicker.defaultValue });
    }
  }, [visible, auditorPicker.locked, auditorPicker.defaultValue, form]);

  const reportError = (msg: string) => {
    setSubmitError(msg);
    notify.error(msg);
  };

  const handleSubmit = async (values: WaypointFormValues) => {
    setSubmitError(null);
    if (fileList.length === 0) {
      reportError(
        t("waypoint.gpx_file_required") || "El archivo GPX es requerido",
      );
      return;
    }
    const file = fileList[0].originFileObj as File | undefined;
    if (!file) {
      reportError(
        "No se pudo leer el archivo seleccionado — selecciónalo de nuevo.",
      );
      // eslint-disable-next-line no-console
      console.warn(
        "[waypoints upload] fileList[0].originFileObj is undefined — antd Upload state:",
        fileList,
      );
      return;
    }
    // eslint-disable-next-line no-console
    console.info(
      "[waypoints upload] sending file:",
      file.name,
      file.size,
      "bytes — auditor_id=",
      values.auditor_id,
      "waypoint_date=",
      values.waypoint_date.format("YYYY-MM-DD"),
    );

    setUploading(true);
    try {
      const result = await waypointService.uploadWaypoints(planningId, {
        gpx_file: file,
        auditor_id: values.auditor_id,
        waypoint_date: values.waypoint_date.format("YYYY-MM-DD"),
      });

      const baseMsg =
        t("waypoint.upload_success") || "Waypoints subidos correctamente";
      const detail = `${result.waypoints_count ?? 0} ${(
        t("waypoint.title") || "waypoints"
      ).toLowerCase()}`;
      const samplesNote =
        result.updated_samples > 0
          ? ` · ${result.updated_samples} ${t("audit.samples") || "muestras"} ${
              t("common.updated") || "actualizadas"
            }`
          : "";
      notify.success(`${baseMsg} (${detail}${samplesNote})`);

      form.resetFields();
      setFileList([]);
      setSubmitError(null);
      onClose();
      onSuccess?.();
    } catch (error: any) {
      // Pull the error message out of the various shapes axios + our
      // interceptor wrap the response in. Drop the user on a banner
      // inside the modal so it stays visible until they retry.
      const data =
        error?.originalError?.response?.data ??
        error?.response?.data ??
        null;
      const detail =
        (typeof data === "string" ? data : null) ||
        data?.error ||
        data?.detail ||
        (Array.isArray(data?.non_field_errors)
          ? data.non_field_errors.join("\n")
          : null) ||
        error?.message ||
        t("waypoint.upload_error") ||
        "Error al subir los waypoints";
      // eslint-disable-next-line no-console
      console.error("[waypoints upload] failed:", error, "response:", data);
      reportError(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setSubmitError(null);
    onClose();
  };

  const beforeUpload = (file: File) => {
    const isGPX = file.name.toLowerCase().endsWith(".gpx");
    if (!isGPX) {
      reportError(
        t("route.invalid_gpx") || "Solo se permiten archivos GPX (.gpx)",
      );
      return Upload.LIST_IGNORE;
    }
    setSubmitError(null);
    return false; // Prevent auto upload — handled in handleSubmit
  };

  const handleFileChange = (info: any) => {
    // Limit to 1 file; antd UploadFile wrappers carry `originFileObj`.
    setFileList(info.fileList.slice(-1));
  };

  return (
    <Modal
      title={t("waypoint.upload_waypoints") || "Subir Waypoints"}
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={t("common.save") || "Guardar"}
      cancelText={t("common.cancel") || "Cancelar"}
      confirmLoading={uploading}
      width={600}
    >
      {submitError && (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setSubmitError(null)}
          style={{ marginBottom: 16, whiteSpace: "pre-line" }}
          message={t("waypoint.upload_error") || "Error al subir los waypoints"}
          description={submitError}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          name="waypoint_date"
          label={t("waypoint.waypoint_date") || "Fecha de Captura"}
          rules={[
            {
              required: true,
              message:
                t("waypoint.date_required") || "La fecha es obligatoria",
            },
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder={
              t("waypoint.date_placeholder") || "Selecciona la fecha"
            }
            getPopupContainer={(trigger) =>
              trigger.parentElement || document.body
            }
          />
        </Form.Item>

        <Form.Item
          name="auditor_id"
          label={t("waypoint.auditor") || "Auditor"}
          rules={[
            {
              required: true,
              message:
                t("waypoint.auditor_required") || "Selecciona un auditor",
            },
          ]}
        >
          <Select
            placeholder={t("waypoint.select_auditor") || "Seleccionar Auditor"}
            showSearch
            optionFilterProp="label"
            loading={auditorPicker.loading}
            disabled={auditorPicker.locked}
            getPopupContainer={(trigger) =>
              trigger.parentElement || document.body
            }
            options={auditorPicker.options}
          />
        </Form.Item>

        <Form.Item
          label={t("waypoint.gpx_file") || "Archivo GPX"}
          required
          validateStatus={fileList.length === 0 ? undefined : "success"}
          // No `name` on purpose: the file lives in `fileList` state.
          // Wiring it through antd Form here previously left the form
          // value as the raw change-event object, which broke handlers
          // and confused validation timing. We re-check in handleSubmit.
        >
          <Upload
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={handleFileChange}
            maxCount={1}
            accept=".gpx"
          >
            <Button icon={<UploadOutlined />}>
              {t("route.select_gpx_file") || "Seleccionar Archivo GPX"}
            </Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};
